"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { mainNav, site } from "@/lib/site";
import { Container } from "./Container";

// Fixed nav, context-aware: white logo + white type over the dark hero on the
// homepage (solidifying to a slate bar on scroll); ink-on-paper on every other
// page and inside the mobile menu. Solutions & Locations open dropdowns.
export function Header({
  c = {},
  cityNav = [],
}: {
  c?: Record<string, string>;
  /** Published cities from /admin/locations — replaces the static Locations
   * dropdown so a city added in the admin panel shows up in the menu. */
  cityNav?: { label: string; href: string }[];
} = {}) {
  const pathname = usePathname();
  const isHome = pathname === "/";
  const mounted = useRef(false);
  useEffect(() => {
    // Suppress on a real route CHANGE only. This effect also runs on mount,
    // and suppressing there left the dropdowns dead on first load until the
    // pointer happened to enter and leave a menu — the "doesn't open the
    // first time" bug.
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setDdSuppressed(true); // route changed — hide any open dropdown
    setDdOpen(null);
  }, [pathname]);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // which mobile submenu (by href) is expanded
  const [subOpen, setSubOpen] = useState<string | null>(null);
  // Desktop dropdowns are hover-driven; after a client-side navigation the
  // pointer is still over the menu, so :hover keeps it open on the new page.
  // Suppress the dropdown on click/route-change until the pointer leaves.
  const [ddSuppressed, setDdSuppressed] = useState(false);
  // Touch has no hover: the first tap on Solutions/Locations must OPEN the
  // dropdown (not navigate), so tap-opens are tracked as state. A second tap
  // on the open trigger follows the link; tapping elsewhere closes it.
  const [ddOpen, setDdOpen] = useState<string | null>(null);
  const lastPointer = useRef("mouse");

  // Close a tap-opened dropdown when the tap lands outside the nav.
  useEffect(() => {
    if (!ddOpen) return;
    const onDown = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.("[data-nav-dd]")) setDdOpen(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [ddOpen]);

  // Lift suppression as soon as the pointer is anywhere outside a dropdown.
  // Previously only the dropdown's own onMouseLeave cleared it, so navigating
  // via any other link (About, a card, the logo) left every dropdown dead
  // until the pointer happened to cross one — the "sometimes doesn't open" bug.
  useEffect(() => {
    if (!ddSuppressed) return;
    const onMove = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.("[data-nav-dd]")) setDdSuppressed(false);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => document.removeEventListener("pointermove", onMove);
  }, [ddSuppressed]);

  // The Locations dropdown lists whatever is published in /admin/locations;
  // lib/site's hardcoded city list is only the no-DB fallback.
  const nav = cityNav.length
    ? mainNav.map((item) =>
        item.href === "/locations" && item.children ? { ...item, children: cityNav } : item,
      )
    : mainNav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setSubOpen(null);
  }, [pathname]);

  // The admin console has its own left-sidebar navigation (app/admin/layout).
  if (pathname.startsWith("/admin")) return null;

  // Dark bar + white logo on every page (matching the homepage), except while
  // the mobile menu is open — that panel is light, so the header goes light too.
  const dark = !open;
  const solid = scrolled || !isHome || open;

  const barClass = !solid
    ? "border-b border-transparent bg-transparent"
    : dark
      ? "border-b border-white/10 bg-brand-dark/90 backdrop-blur-md"
      : "border-b border-brand-line bg-brand-paper/90 backdrop-blur-md";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        dark ? "text-white" : "text-brand-ink"
      } ${barClass}`}
    >
      <Container className="flex h-16 items-center justify-between md:h-20">
        <Link href="/" className="relative z-10 flex items-center" aria-label={site.name}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={
              dark
                ? c["logo.dark"]?.trim() || "/logo-white.png"
                : c["logo.light"]?.trim() || "/logo.png"
            }
            alt={c["logo.dark.alt"]?.trim() || site.name}
            data-cms-img={dark ? "logo.dark" : "logo.light"}
            className="h-6 w-auto md:h-7"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {nav.map((item, i) =>
            item.children ? (
              <div
                key={item.href}
                data-nav-dd
                className="group relative"
                // Releases a tap-opened panel, which is pinned with
                // !visible and ignores hover by design — that pin is what lets
                // a touch user tap the trigger, read the list, then tap an
                // item. A device with no pointer to leave with never fires
                // this, so the tap behaviour it exists for is untouched.
                //
                // It deliberately does NOT clear ddSuppressed. CSS :hover does
                // not update until the pointer MOVES, and hiding the panel
                // fires mouseleave on its own — so clearing suppression here
                // handed the still-stale :hover back to group-hover, and the
                // dropdown reopened over the page just navigated to and stayed
                // there until the mouse happened to move. The pointermove
                // listener above lifts suppression instead, by which time
                // :hover is accurate.
                onMouseLeave={() => setDdOpen(null)}
              >
                <Link
                  href={item.href}
                  aria-expanded={ddOpen === item.href}
                  onPointerDown={(e) => {
                    lastPointer.current = e.pointerType;
                  }}
                  onClick={(e) => {
                    // Touch/pen taps open the dropdown first; only a second
                    // tap on the open trigger follows the link. Mouse clicks
                    // navigate as before (hover already shows the menu).
                    if (lastPointer.current !== "mouse" && ddOpen !== item.href) {
                      e.preventDefault();
                      setDdSuppressed(false);
                      setDdOpen(item.href);
                    }
                  }}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    dark ? "text-white/80 hover:text-white" : "text-brand-slate hover:text-brand-ink"
                  }`}
                >
                  <span data-cms-key={`nav.${i}.label`}>{c[`nav.${i}.label`]?.trim() || item.label}</span>
                  <svg
                    className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    aria-hidden
                  >
                    <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
                {/* dropdown */}
                <div
                  className={`invisible absolute left-1/2 top-full z-20 w-60 -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 ${
                    ddSuppressed
                      ? "pointer-events-none"
                      : ddOpen === item.href
                        ? "!visible !opacity-100"
                        : "group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                  }`}
                >
                  <div className="overflow-hidden rounded-xl border border-brand-line bg-brand-paper p-2 shadow-lg shadow-black/5">
                    {item.children.map((child, j) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        // Link navigates inside a transition, and React
                        // defers state updates made in one until the new route
                        // is ready. Closing on click therefore did not land
                        // until the page had already changed, and with the
                        // pointer still resting inside the group, group-hover
                        // held the dropdown open across the navigation and
                        // kept it open afterwards until the mouse moved away.
                        //
                        // flushSync applies it before the transition starts.
                        // Closing on pointerdown instead looks equivalent and
                        // is not: it hides the element between pointerdown and
                        // click, so the link never receives the click and the
                        // menu stops navigating at all.
                        onClick={() => {
                          flushSync(() => {
                            setDdSuppressed(true);
                            setDdOpen(null);
                          });
                        }}
                        className="block rounded-lg px-3 py-2.5 text-sm text-brand-slate transition-colors hover:bg-brand-surface hover:text-brand-accent"
                      >
                        <span data-cms-key={`nav.${i}.sub.${j}.label`}>{c[`nav.${i}.sub.${j}.label`]?.trim() || child.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className={`group relative text-sm font-medium transition-colors ${
                  dark ? "text-white/80 hover:text-white" : "text-brand-slate hover:text-brand-ink"
                }`}
              >
                <span data-cms-key={`nav.${i}.label`}>{c[`nav.${i}.label`]?.trim() || item.label}</span>
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-brand-accent transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          {/* Always visible, including on phones; hidden only while the mobile
              menu is open (the overlay has its own CTA). */}
          {!open && (
          <>
            {/* Desktop: full CTA. Mobile: an envelope icon button. */}
            <Link
              href="/contact-us"
              className="btn btn-primary btn-sm hidden! sm:inline-flex!"
            >
              <span data-cms-key="cta.label">{c["cta.label"]?.trim() || "Enquire now"}</span>
            </Link>
            <Link
              href="/contact-us"
              aria-label="Enquire now"
              className="btn btn-primary btn-sm inline-flex! h-10 w-10 items-center justify-center px-0! sm:hidden!"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
            </Link>
          </>
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="relative z-10 flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
          >
            <span
              className={`block h-px w-6 bg-current transition-all duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`}
            />
            <span
              className={`block h-px w-6 bg-current transition-all duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`}
            />
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-16 h-[calc(100dvh-4rem)] overflow-y-auto bg-brand-paper lg:hidden"
          >
            <Container className="flex flex-col gap-1 py-8">
              {nav.map((item, i) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-brand-line"
                >
                  {item.children ? (
                    <>
                      {/* The whole row toggles the submenu — tapping the word
                          "Locations"/"Solutions" must open the list, not
                          navigate. The hub page stays reachable through the
                          "View all" entry inside the expanded list. */}
                      <button
                        type="button"
                        onClick={() =>
                          setSubOpen((v) => (v === item.href ? null : item.href))
                        }
                        aria-expanded={subOpen === item.href}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <span className="flex-1 py-4 text-2xl font-medium text-brand-ink">
                          <span data-cms-key={`nav.${i}.label`}>{c[`nav.${i}.label`]?.trim() || item.label}</span>
                        </span>
                        <span className="flex h-11 w-11 items-center justify-center text-brand-accent">
                          <svg
                            className={`h-4 w-4 transition-transform duration-300 ${
                              subOpen === item.href ? "rotate-180" : ""
                            }`}
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden
                          >
                            <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {subOpen === item.href && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="flex flex-col gap-1 pb-4 pl-1">
                              {item.children.map((child, j) => (
                                <Link
                                  key={child.href}
                                  href={child.href}
                                  // Same deferral as the desktop dropdown, and
                                  // the same reason for flushSync over
                                  // pointerdown — the tap has to reach the link.
                                  onClick={() => {
                                    flushSync(() => {
                                      setOpen(false);
                                      setSubOpen(null);
                                    });
                                  }}
                                  className="py-1.5 text-base text-brand-slate transition-colors hover:text-brand-accent"
                                >
                                  <span data-cms-key={`nav.${i}.sub.${j}.label`}>{c[`nav.${i}.sub.${j}.label`]?.trim() || child.label}</span>
                                </Link>
                              ))}
                              {/* The row itself now toggles the list, so the
                                  hub page needs its own entry. */}
                              <Link
                                href={item.href}
                                className="py-1.5 text-base font-medium text-brand-accent"
                              >
                                View all {c[`nav.${i}.label`]?.trim() || item.label}
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      href={item.href}
                      className="block py-4 text-2xl font-medium text-brand-ink"
                    >
                      <span data-cms-key={`nav.${i}.label`}>{c[`nav.${i}.label`]?.trim() || item.label}</span>
                    </Link>
                  )}
                </motion.div>
              ))}
              <Link href="/contact-us" className="btn btn-primary mt-6 w-fit">
                <span data-cms-key="cta.label">{c["cta.label"]?.trim() || "Enquire now"}</span>
              </Link>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
